/* eslint-disable react-hooks/exhaustive-deps */
import FranquiciasCompGrid from 'components/Franquicias/FranquiciasCompGrid'
import { useUser } from 'contexts/UserContext'
import useFetch from 'hooks/useFetch'
import { useToast } from 'hooks/useToast'
import { useCallback, useEffect } from 'react'
import XLSX from 'xlsx-js-style'

const SHEET_NAME = 'Franquicias Operativas'

// NO trim: respetar espacios
const toStr = (v) => (v === null || v === undefined ? '' : String(v))

// Para detectar fila vacía, aquí sí usamos trim SOLO para comprobar si está vacía
const isEmptyRow = (row) =>
  Object.values(row || {}).every((v) => toStr(v).trim() === '')

// Mapeo de cabeceras del Excel -> campos API (gestor eliminado)
const HEADER_MAP = {
  // exactos
  codigo_franquicia: 'codigo_franquicia',
  codigo_fq_3dig: 'codigo_fq_3dig',
  nombre: 'nombre',
  propiedad_franquicia: 'propiedad_franquicia',
  cif_nif: 'cif_nif',
  iban: 'iban',
  columna1: 'columna1',
  columna2: 'columna2',

  // aliases comunes
  'Código franquicia': 'codigo_franquicia',
  'Codigo franquicia': 'codigo_franquicia',
  'CODIGO FRANQUICIA': 'codigo_franquicia',
  'Código': 'codigo_franquicia',
  'CODIGO': 'codigo_franquicia',

  'Código fq 3dig': 'codigo_fq_3dig',
  'Codigo fq 3dig': 'codigo_fq_3dig',
  'Código fq 3 dig': 'codigo_fq_3dig',
  'Codigo fq 3 dig': 'codigo_fq_3dig',
  'Cod. 3D': 'codigo_fq_3dig',
  'COD. 3D': 'codigo_fq_3dig',
  'Cod 3D': 'codigo_fq_3dig',

  Nombre: 'nombre',
  NOMBRE: 'nombre',

  'Propiedad franquicia': 'propiedad_franquicia',
  'Propiedad Franquicia': 'propiedad_franquicia',
  'PROPIEDAD FRANQUICIA': 'propiedad_franquicia',
  Propiedad: 'propiedad_franquicia',

  'CIF/NIF': 'cif_nif',
  'Cif/Nif': 'cif_nif',
  CIF: 'cif_nif',
  NIF: 'cif_nif',

  IBAN: 'iban',
  Iban: 'iban',

  email: 'email',
  Email: 'email',
  EMAIL: 'email',
  'Email Franquicia': 'email',
  'email franquicia': 'email',
  'EMAIL FRANQUICIA': 'email',
  'Correo': 'email',
  'Correo electrónico': 'email',
  'CORREO': 'email',

  Columna1: 'columna1',
  Columna2: 'columna2',
  COLUMNA1: 'columna1',
  COLUMNA2: 'columna2'
}

// SOLO obligatorio:
const REQUIRED = ['codigo_franquicia']

export default function FranquiciasViewGrid() {
  const { showToast } = useToast()
  const { user } = useUser()

  const [fetched, fetchAll] = useFetch()
  const [deleted, deleteOne] = useFetch()

  const handleRefresh = async () => {
    await fetchAll({
      url: 'franquicias',
      method: 'GET',
      messageKo: '¡Error cargando franquicias!'
    })
  }

  useEffect(() => {
    handleRefresh()
  }, [])

  const handleDelete = async (codigoFranquicia) => {
    await deleteOne({
      url: `franquicias/${codigoFranquicia}`,
      method: 'DELETE',
      messageKo: '¡Error eliminando franquicia!'
    })
  }

  useEffect(() => {
    if (!deleted.isCalled) return
    if (deleted.message) {
      showToast(deleted.message, 'success')
      handleRefresh()
    }
    if (deleted.error) showToast(deleted.error, 'error')
  }, [deleted.loading])

  // ===== Analizar Excel (para el modal por pasos) =====
  const handleAnalyzeExcel = useCallback(async (file) => {
    if (!file) throw new Error('No se ha seleccionado ningún archivo.')
    if (!file.name?.toLowerCase().endsWith('.xlsx')) throw new Error('El archivo debe ser .xlsx')

    const buffer = await file.arrayBuffer()
    const workbook = XLSX.read(buffer, { type: 'array' })

    const sheetName = workbook.SheetNames?.includes(SHEET_NAME)
      ? SHEET_NAME
      : workbook.SheetNames?.[0]

    if (!sheetName) throw new Error('El archivo Excel no contiene hojas.')

    const sheet = workbook.Sheets[sheetName]
    if (!sheet) throw new Error(`No se pudo leer la hoja: ${sheetName}`)

    const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: false })
    if (!rawRows?.length) throw new Error('La hoja está vacía.')

    const logs = []
    if (sheetName !== SHEET_NAME) {
      logs.push({
        type: 'warning',
        message: `No se encontró la hoja "${SHEET_NAME}". Se usará "${sheetName}".`
      })
    }

    // Detecta columnas disponibles
    const excelHeaders = Object.keys(rawRows?.[0] || {})
    const mappedHeaders = excelHeaders
      .map((h) => HEADER_MAP[h] || HEADER_MAP[toStr(h)] || null)
      .filter(Boolean)

    // Verifica obligatorias (solo codigo_franquicia)
    for (const req of REQUIRED) {
      if (!mappedHeaders.includes(req)) {
        logs.push({
          type: 'error',
          message: `Falta la columna obligatoria en Excel: "${req}". (Revisa cabeceras)`
        })
      }
    }

    if (logs.some((l) => l.type === 'error')) {
      return { logs, data: [] }
    }

    const data = []
    let emptySkipped = 0

    rawRows.forEach((row, idx) => {
      if (isEmptyRow(row)) {
        emptySkipped++
        return
      }

      // gestor eliminado; el resto puede ir vacío
      const out = {
        codigo_franquicia: '',
        codigo_fq_3dig: '',
        nombre: '',
        propiedad_franquicia: '',
        cif_nif: '',
        iban: '',
        email: '',
        columna1: '',
        columna2: ''
      }

      for (const [excelKey, value] of Object.entries(row)) {
        const targetKey = HEADER_MAP[excelKey] || HEADER_MAP[toStr(excelKey)]
        if (!targetKey) continue

        // NO normalizamos iban ni hacemos trim
        out[targetKey] = toStr(value)
      }

      // ÚNICA validación: codigo_franquicia no puede estar vacío (aquí sí trim para validar)
      const codigo = toStr(out.codigo_franquicia).trim()
      if (!codigo) {
        logs.push({
          type: 'error',
          message: `Fila ${idx + 2}: falta codigo_franquicia.`
        })
        return
      }

      data.push(out)
      logs.push({
        type: 'import',
        message: `Fila ${idx + 2}: OK (${codigo})`
      })
    })

    if (emptySkipped > 0) {
      logs.push({
        type: 'warning',
        message: `Se han omitido ${emptySkipped} filas vacías.`
      })
    }

    if (!data.length) {
      logs.push({
        type: 'error',
        message: 'No hay filas válidas para importar.'
      })
    }

    return { logs, data }
  }, [])

  return (
    <FranquiciasCompGrid
      user={user}
      data={fetched?.data}
      handleRefresh={handleRefresh}
      handleDelete={handleDelete}
      handleAnalyzeExcel={handleAnalyzeExcel}
    />
  )
}
